import get from 'lodash/get';
import { useCallback, useMemo, useState } from 'react';
import { PanelGroup } from 'rsuite';
import { ButtonExpandAll } from './button-expand-all';
import { Panel } from './styled-components';
import { ConfigItem, SupplementalQuestionsAccordionProps } from './types';
import { EXPAND_ALL_PANEL_EVENT_KEY, isNotNil, valueCleaner } from './utils';

/**
 * A component that displays supplemental questions and answers in an accordion format.
 *
 * The component will not render if:
 *  1. When "propertyDetails.tabs.license.config.secondaryDisplay" is undefined/null/[]
 *  2. When there are no supplemental question/answer (SQA) sets to render (items = [])
 *      Determined by "labelToDisplay" (as the display value will fallback to "N/A")
 *        Where "labelToDisplay" is the question that corresponds to some answer the user provided
 *        If false-y, then the SQA set being iterated upon will not be rendered
 */
export function SupplementalQuestionsAccordion(
  props: SupplementalQuestionsAccordionProps
) {
  const { license, config = [] } = props;

  const [isExpandAll, setIsExpandAll] = useState<boolean>(false);
  const [activePanelGroupKey, setActivePanelGroupKey] = useState<
    number | undefined
  >();

  const onPanelClick = useCallback((idx: number): void => {
    setIsExpandAll(false);
    setActivePanelGroupKey(idx);
  }, []);

  const onExpandAllClick = () => {
    if (!isExpandAll) {
      setActivePanelGroupKey(EXPAND_ALL_PANEL_EVENT_KEY);
    }
    setIsExpandAll((prev) => !prev);
  };

  /**
   * Safely gets a value from the license object using a string key
   */
  const safeGetFromLicense = useCallback(
    (key: string | undefined, defaultValue?: string | undefined): unknown => {
      if (typeof key !== 'string') {
        return defaultValue;
      }
      return get(license, key, defaultValue);
    },
    [license]
  );

  /**
   * Extracts the appropriate key from dataKey which can be a string or array of strings
   */
  const getEffectiveKey = useCallback(
    (dataKey: unknown): string => {
      if (Array.isArray(dataKey)) {
        // Find the first key that returns a non-empty value
        const foundKey = dataKey.find((k) => {
          if (typeof k !== 'string') return false;
          return Boolean(valueCleaner(get(license, k), undefined));
        });

        // Return the found key or the first key as fallback
        return typeof foundKey === 'string' ? foundKey : dataKey[0] || '';
      }

      return typeof dataKey === 'string' ? dataKey : '';
    },
    [license]
  );

  const items = useMemo(
    () =>
      config
        .map(
          (
            { dataKey, label, labelKey, map, hideNoAnswer }: ConfigItem,
            i: number
          ) => {
            const key = getEffectiveKey(dataKey);
            const value = valueCleaner(get(license, key), map);
            const labelToDisplay = safeGetFromLicense(labelKey, label);

            if (!labelToDisplay || (hideNoAnswer && value === '')) {
              return null;
            }

            return (
              <Panel
                onClick={() => onPanelClick(i)}
                header={labelToDisplay}
                eventKey={isExpandAll ? EXPAND_ALL_PANEL_EVENT_KEY : i}
                expanded={isExpandAll || i === activePanelGroupKey}
                id={`panel${i}`}
                key={key}
              >
                <div>{value}</div>
              </Panel>
            );
          }
        )
        .filter(isNotNil),
    [
      activePanelGroupKey,
      config,
      isExpandAll,
      license,
      onPanelClick,
      safeGetFromLicense,
      getEffectiveKey,
    ]
  );

  if (!items.length) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h5>Supplemental Questions</h5>
        <ButtonExpandAll
          className="max-w-40"
          onExpandAllClick={onExpandAllClick}
          isExpandAll={isExpandAll}
        />
      </div>

      <PanelGroup
        accordion
        bordered
        activeKey={activePanelGroupKey}
        aria-label="Supplemental questions panels"
      >
        {items}
      </PanelGroup>
    </div>
  );
}
